'use client'
import { useEffect, useState, useRef, useCallback } from 'react'
import Image from 'next/image'
import dataLoader from '@/utils/fetchSingleType'
import { MdArrowBackIosNew } from "react-icons/md";
import { MdArrowForwardIos } from "react-icons/md";
import { Autoplay } from 'swiper/modules';
import Link from 'next/link';
import { getBaseUrl, getPublicBaseUrl } from '@/lib/getBaseURL';
import { useLanguage } from '@/app/LanguageContext';
import { useTranslate } from '@/utils/languageTranslation';

interface HeroBlock {
  title: string
  subTitle: string
  heroBackground: {
    url: string
  }
}

interface HomePage {
  heroBlock: HeroBlock[]
}

export default function HeroSlider() {
  const {t} = useTranslate()
  const { language } = useLanguage();
  const [data, setData] = useState<HeroBlock[]>([])
  const [currentSlide, setCurrentSlide] = useState(0)
  const [animating, setAnimating] = useState(false)
  const [autoPlay, setAutoPlay] = useState(true)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const sliderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchData = async () => {
      const response = await dataLoader<HomePage>('home-page', language)
      if (response?.heroBlock) {
        setData(response.heroBlock)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (data.length === 0 || !autoPlay) return

    const interval = setInterval(() => {
      setAnimating(true)
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % data.length)
        setAnimating(false)
      }, 600) // slide animation duration
    }, 5000)

    return () => clearInterval(interval)
  }, [data, autoPlay, currentSlide])

  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % data.length)
  }, [data.length])

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + data.length) % data.length)
  }, [data.length])

  const handleArrowClick = (direction: 'prev' | 'next') => {
    // Pause auto-play when user interacts with arrows
    setAutoPlay(false)
    setAnimating(true)
    
    setTimeout(() => {
      if (direction === 'prev') {
        goToPrevSlide()
      } else {
        goToNextSlide()
      }
      setAnimating(false)
      
      const resumeTimer = setTimeout(() => setAutoPlay(true), 5000);
      return () => clearTimeout(resumeTimer);
    }, 300)
  }

  // Touch event handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setAutoPlay(false)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Swipe left (next slide)
      setAnimating(true)
      setTimeout(() => {
        goToNextSlide()
        setAnimating(false)
      }, 300)
    }

    if (touchStart - touchEnd < -50) {
      // Swipe right (previous slide)
      setAnimating(true)
      setTimeout(() => {
        goToPrevSlide()
        setAnimating(false)
      }, 300)
    }

    // Resume autoplay after swipe
    const resumeTimer = setTimeout(() => setAutoPlay(true), 5000);
    return () => clearTimeout(resumeTimer);
  }

  const current = data[currentSlide]

  if (!current) return null

  return (
    <div 
      className="relative h-[14rem] lg:min-h-[32rem] xl:h-[35.5625rem] w-full bg-cover bg-center transition-opacity duration-1000"
      style={{
        backgroundImage: `url(${getPublicBaseUrl()}${current.heroBackground?.url})`,
        opacity: animating ? 0.75 : 1
      }}
      ref={sliderRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className='relative bg-gradient-to-r from-[#002B4E] via-[#002B4ECA] via-[38.97%] to-[#73737300] to-[99.27%] lg:h-[32rem] xl:h-[35.5625rem] h-[14rem] w-full z-10'>
        <div className='md:max-w-[71rem] w-full h-full mx-auto flex justify-start items-center'>
          <div className="w-full xl:w-[53.34rem] md:max-h-[19.25rem] max-h-[75%] flex flex-col text-center lg:text-left my-auto justify-between px-4 lg:px-10 xl:px-0">
            <h1 className="text-white text-xl md:text-2xl lg:text-5xl lg:text-[3.75rem] font-bold text-center lg:text-left">
              {current.title}
            </h1>
            <p className="font-medium text-sm lg:text-[1.25rem] text-white lg:pr-44 items-center justify-center lg:justify-start mt-2 md:mt-4 flex flex-1">
              {current.subTitle}
            </p>
            <Link href="/service" className='px-2 py-1 md:p-0 w-fit md:w-[12rem] mt-1 md:mt-3 h-fit md:h-10 mx-auto lg:mx-0 bg-white hover:scale-105 text-xs md:text-lg rounded-full cursor-pointer transition-transform flex items-center justify-center'>
              {t('explore_services')}
            </Link>
          </div>
        </div>
        
        {/* Arrow buttons - hidden on mobile, visible on md and up */}
        <div className='hidden md:flex absolute w-full max-w-[82rem] h-[3.63rem] top-[50%] -translate-y-1/2 left-1/2 -translate-x-1/2 items-center justify-between'>
          <button 
            onClick={() => handleArrowClick('prev')} 
            className='cursor-pointer focus:outline-none'
            aria-label="Previous slide"
          >
            <MdArrowBackIosNew className='text-white md:text-2xl lg:text-3xl xl:text-5xl hover:text-slate-300'/>
          </button>
          <button 
            onClick={() => handleArrowClick('next')} 
            className='cursor-pointer focus:outline-none'
            aria-label="Next slide"
          >
            <MdArrowForwardIos className='text-white md:text-2xl lg:text-3xl xl:text-5xl hover:text-slate-300'/>
          </button>
        </div>
      </div>
      
      {/* Mobile indicators */}
      <div className="md:hidden absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {data.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full ${currentSlide === index ? 'bg-white' : 'bg-gray-400'}`}
          />
        ))}
      </div>
    </div>
  )
}